<template>
  <FormFieldwrap>
    <input
      v-bind="$attrs"
      :id="id"
      :class="[
        $style.base,
        showErrorMessage ? $style.invalid : {},
        type === 'number' ? $style['input-number'] : {}
      ]"
      :name="name"
      :placeholder="placeholder"
      :type="type"
      :readonly="readonly"
      :disabled="disabled"
      :value="value"
      @input="onChange"
      @blur="onBlur"
      @keyup.enter="$emit('submit', ($event.target as HTMLInputElement).value)"
    >
    <BaseLabel :for="id">
      {{ formattedLabel }}
    </BaseLabel>
    <div
      :v-if="showErrorMessage"
      :class="[
        $style['base-validation-message'],
        showErrorMessage ? $style['show-validation-message'] : {}
      ]"
      data-pw="error-message"
    >
      {{ errorMessage }}
    </div>
  </FormFieldwrap>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useField } from 'vee-validate'
import type { Schema } from 'yup'
import { boolean, date, number, string, StringSchema } from 'yup'
import FormFieldwrap from '~/base/components/form/FormFieldwrap.vue'
import BaseLabel from '~/base/components/label/Label.vue'

const emits = defineEmits(['update:modelValue', 'submit'])
const props = defineProps({
  type: { type: String, default: 'text' },
  readonly: { type: Boolean },
  id: { type: String, default: '' },
  name: { type: String, required: true },
  label: { type: String, required: true },
  modelValue: { type: String, required: true },
  disabled: { type: Boolean },
  placeholder: { type: String, default: '' },
  required: { type: Boolean, default: false },
  optional: { type: Boolean, default: false }, // adds "optional" as suffix to the input label
  error: { type: String, required: false, default: null },
  rules: { type: StringSchema, default: null }
})
const typeYupMapping: Record<string, Schema> = {
  text: string(),
  number: number(),
  checkbox: boolean(),
  date: date()
}

const adjustedRules = computed(() => {
  return props.required
    ? props.rules
      ? props.rules
      : typeYupMapping[props.type].required()
    : props.rules
})

const {
  value,
  errorMessage: validationError,
  meta,
  handleChange,
  setTouched,
  validate
} = useField(props.name, adjustedRules, {
  initialValue: props.modelValue
})

const showErrorMessage = computed(() => {
  return (!meta.valid || errorMessage.value) && meta.touched
})

const errorMessage = computed(() => {
  return props.error ? props.error : validationError.value
})

const onBlur = () => {
  validate()
  setTouched(true)
}

const onChange = (event: Event) => {
  handleChange(event, true)
  emits('update:modelValue', value.value)
}

const formattedLabel = computed(() => {
  return props.optional ? props.label + ' (optional)' : props.label
})
</script>

<style module>
.base {
  border: 1px solid var(--color-grey3);
  border-radius: var(--radius-default);
  display: block;
  width: 100%;
  height: 55px;
  font-size: clamp(16px, 2vw, 18px);
  line-height: clamp(22px, 2vw, 22px);
  background-color: var(--color-grey2);
  transition: var(--transition-default);
  padding: 26px 12px 7px 12px;
  appearance: none;

  ::placeholder {
    color: var(--color-pacificblue);
    opacity: 1;
  }

  outline-width: var(--focus-outline-width);
  outline-offset: var(--focus-outline-offset);
  outline-color: var(--color-pacificblue);
  :focus {
    border-color: var(--color-pacificblue);
    background-color: var(--color-white);
  }

  &:disabled,
  &:read-only {
    background: var(--color-grey2);
    color: var(--color-jet-disabled);
    opacity: 0.2;
  }
}

.input-number {
  appearance: none;
}

.invalid {
  border-color: var(--color-alert);
  outline-color: var(--color-alert);
  color: var(--color-alert);
  transition: none;
  background-image: url(~/base/assets/icons/alert.svg);
  background-color: var(--color-white);
  background-repeat: no-repeat;
  background-position: right;
  background-position-x: calc(100% - 16px);
}

.base-validation-message {
  color: transparent;
  padding: 0;
  font-size: 0px;
  line-height: 0px;
  transition:
    opacity 0.1s ease-in-out 0s, padding 0.3s ease-in-out 0.1s, font-size 0.3s ease-in-out 0.1s, line-height 0.3s ease-in-out 0.1s;
  opacity: 0;
}

.show-validation-message {
  font-size: 14px;
  line-height: 17px;
  transition:
    font-size 0.3s ease-in-out 0s, line-height 0.3s ease-in-out 0s, padding 0.3s ease-in-out 0s, opacity 0.1s ease-in-out 0.3s;
  opacity: 1;
  color: var(--color-alert);
  padding: 8px 0;
}
</style>
